<template>
    <div class="navbar__top" data-testid="navbar-top-modes">
        <div class="navbar-container">
            <ul v-if="hasMultipleModes" class="navbar__top-modes">
                <li v-for="availableMode in moduleConfig.modes" :key="availableMode.id" name="navbar-mode-tab">
                    <NuxtLink
                        :to="$withLocalePrefix(availableMode.page)"
                        :class="{active: selectedMode === availableMode.id}"
                        :data-testid="`navbar-mode-tab-${availableMode.id}`"
                        :aria-current="selectedMode === availableMode.id ? 'page' : undefined"
                        aria-controls="navbar-content"
                        @click="switchMode(availableMode.id)"
                    >
                        <div class="navbar__top-modes-icon">
                            <VJoyIcon :name="availableMode.id === 'client' ? 'company-placeholder' : 'super-malter'" color="neutral-50" />
                            {{ $t(`navbar.top.${availableMode.id}.label` as keyof AllTranslationsRecord) }}
                        </div>
                    </NuxtLink>
                </li>
            </ul>
            <div class="navbar__top-language-selector">
                <LocaleSelection />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {NuxtLink} from '#components';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import {useNavbarMode} from '../composables/useNavbarMode';
    import LocaleSelection from '../features/localization-selection/ui/LocaleSelection.vue';
    import type {AllTranslationsRecord} from '#build/i18n';

    const {config: moduleConfig, hasMultipleModes, selectedMode, switchMode} = useNavbarMode();
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .navbar__top {
        width: 100%;
        height: var(--navbar-top-mobile-height);
        background-color: var(--joy-color-background-default);
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transform: translateY(-20px);
        transition:
            opacity var(--navbar-transition-duration) var(--navbar-transition-easing),
            visibility var(--navbar-transition-duration) var(--navbar-transition-easing),
            transform var(--navbar-transition-duration) var(--navbar-transition-easing);

        > .navbar-container {
            display: flex;
            align-items: center;
            height: 100%;
            gap: var(--joy-core-spacing-2);
        }

        &-modes {
            display: flex;
            list-style-type: none;
            gap: var(--joy-core-spacing-2);
            width: 100%;
            justify-content: center;
            margin: 0;
            padding: 0;

            &-icon {
                display: flex;
                align-items: center;
                gap: var(--joy-core-spacing-2);
            }
        }

        li {
            a {
                @include text.getFontBySize(primary-350);

                display: block;
                padding: var(--joy-core-spacing-2) var(--joy-core-spacing-6);
                color: var(--joy-color-neutral-70);
                text-decoration: none;
                border-radius: var(--joy-core-spacing-6);
                transition: all calc(var(--navbar-transition-duration) * 0.67) var(--navbar-transition-easing);
                background-color: var(--joy-color-neutral-20);
                user-select: none;

                &:hover,
                &.active {
                    background-color: var(--joy-color-neutral-30);
                }

                &:focus-visible {
                    outline: 1px solid var(--joy-color-neutral-40);
                }
            }
        }

        &-language-selector {
            display: none;
        }
    }

    /* NavbarTop is first in the DOM. Only below the desktop breakpoint it is placed under the bar. */
    @include mq.screen_sm {
        .navbar__top {
            position: fixed;
            top: var(--navbar-height);
            left: 0;
            z-index: 1001;
        }
    }

    /* Same fade and slide as the mobile menu. Hidden again inside a submenu. */
    .header-unlogged:has(.navbar__content--open):not(:has(.navbar__submenu--open)) {
        .navbar__top {
            opacity: 1;
            visibility: visible;
            pointer-events: auto;
            transform: translateY(0);
        }
    }

    /* desktop */
    @include mq.screen_md(min) {
        .navbar__top,
        .header-unlogged:has(.navbar__content--open):not(:has(.navbar__submenu--open)) .navbar__top {
            position: static;
            top: auto;
            left: auto;
            z-index: auto;
            width: 100%;
            height: var(--navbar-top-height);
            margin: 0;
            opacity: 1;
            visibility: visible;
            pointer-events: auto;
            transform: none;
            transition: none;
        }

        .header-unlogged:has(.navbar__content--open):not(:has(.navbar__submenu--open)) .navbar__content {
            padding-top: 0;
        }

        .navbar__top {
            &-modes {
                display: flex;
                position: static;
                width: auto;
                padding: 0;
                margin: 0;
                border-bottom: none;
                background-color: transparent;
                justify-content: flex-start;
            }

            &-language-selector {
                display: block;
                margin-left: auto;
            }
        }
    }
</style>
